import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  ClanName,
  DivTooltip,
  Menu,
  Spinner,
  Tooltip,
  TooltipBody,
  TooltipClan,
  TooltipFooter,
} from '@wg/wows-react-uikit';

import settings from '~/settings';
import { DEFAULT_PRESENCE_ANIMATION } from '~/const/animations';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { ClanRec2IsEnabled } from '~/helpers/common';
import { t } from '~/helpers/localization';
import { hasPermission, PERMISSIONS } from '~/roles';

import styles from './OwnClanTitle.scss';

import type { ROLE_NAMES } from '~/roles';
import type { Nullable } from '~/types/generics';

type MenuAction = 'descriptionEdit' | 'nameEdit' | 'clanSettings' | 'leaveClan' | 'rulesDialog';

type MenuItem = {
  value: MenuAction;
  content: string;
  permissionRequired?: PERMISSIONS;
  isDisabled?: boolean;
};

interface OwnClanTitleProps {
  clanName: string;
  hasClanDescription: boolean;
  clanColor: string;
  clanTag: string;
  needMenu: boolean;

  accumulativeClanResource: number;
  clanLevel: number;
  roleName: Nullable<ROLE_NAMES>;
  isRenameInProcess: boolean;

  onClanInfoEditClick: () => void;
  onNameEditClick: () => void;
  onRecommendationsClanSettingsClick: () => void;
  onLeaveClick: () => void;
  onRulesDialogClick: () => void;
}

const OwnClanTitle = ({
  clanName,
  hasClanDescription,
  clanColor,
  clanTag,
  needMenu,
  roleName,
  isRenameInProcess,
  onClanInfoEditClick,
  onNameEditClick,
  onRecommendationsClanSettingsClick,
  onLeaveClick,
  onRulesDialogClick,
}: OwnClanTitleProps) => {
  const [isMenuActive, setIsMenuActive] = useState(false);

  const handleRulesDialogClick = useCallback(() => {
    dwhExport.push(DWH_EVENTS.SUPPLY.VIEW_RULES);
    onRulesDialogClick();
  }, [onRulesDialogClick]);

  const onMenuBlur = useCallback(() => {
    setIsMenuActive(false);
  }, []);

  const onMenuToggled = useCallback((active: boolean) => {
    setIsMenuActive(active);
  }, []);

  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        value: 'nameEdit',
        content: t('Изменить тег и название'),
        permissionRequired: PERMISSIONS.RENAME_CLAN,
        isDisabled: !settings.clanRename.allowed,
      },
    ];

    if (ClanRec2IsEnabled) {
      items.push({
        value: 'clanSettings',
        content: t('Изменить требования клана'),
        permissionRequired: PERMISSIONS.CHANGE_SETTINGS,
        isDisabled: !settings.clanrec2?.isEnabled,
      });
    }

    items.push(
      {
        value: 'descriptionEdit',
        content: hasClanDescription ? t('Изменить описание') : t('Добавить описание'),
        permissionRequired: PERMISSIONS.CHANGE_SETTINGS,
        isDisabled: !settings.editClanDescription.allowed,
      },
      {
        value: 'rulesDialog',
        content: t('Правила кланов'),
        isDisabled: false,
      },
      {
        value: 'leaveClan',
        content: t('Покинуть клан'),
        isDisabled: false,
      },
    );

    return items;
  }, [hasClanDescription]);

  const visibleMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      if (!item.permissionRequired) {
        return true;
      }
      if (item.isDisabled) {
        return false;
      }
      return hasPermission(roleName, item.permissionRequired);
    });
  }, [menuItems, roleName]);

  const onMenuItemChange = useCallback(
    (action: MenuAction) => {
      const callbacks: Record<MenuAction, () => void> = {
        descriptionEdit: onClanInfoEditClick,
        nameEdit: onNameEditClick,
        clanSettings: onRecommendationsClanSettingsClick,
        leaveClan: onLeaveClick,
        rulesDialog: handleRulesDialogClick,
      };

      callbacks[action]?.();
    },
    [onClanInfoEditClick, onNameEditClick, onRecommendationsClanSettingsClick, onLeaveClick, handleRulesDialogClick],
  );

  const titleTooltip = useMemo(() => {
    if (!needMenu || isMenuActive) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipBody>
          <TooltipClan clanTag={clanTag} clanColor={clanColor} clanName={clanName} />
        </TooltipBody>
        <TooltipFooter mouseIcon="left">{t('Выбрать действие с кланом')}</TooltipFooter>
      </Tooltip>
    );
  }, [needMenu, isMenuActive, clanTag, clanColor, clanName]);

  const titleElement = useMemo(
    () => (
      <DivTooltip className={styles.title} tooltipBody={titleTooltip}>
        <ClanName clanColor={clanColor} clanTag={clanTag} clanName={clanName} />
      </DivTooltip>
    ),
    [titleTooltip, clanColor, clanTag, clanName],
  );

  const displayElement = useMemo(() => {
    if (!needMenu) {
      return titleElement;
    }

    return (
      <Menu<MenuAction>
        // @ts-ignore No need to select items in this menu
        itemSelected={''}
        items={visibleMenuItems}
        onItemChange={onMenuItemChange}
        onMenuBlur={onMenuBlur}
        onMenuToggled={onMenuToggled}
        classNameMenu={styles.menu}
      >
        {titleElement}
      </Menu>
    );
  }, [needMenu, onMenuBlur, onMenuItemChange, onMenuToggled, titleElement, visibleMenuItems]);

  const spinner = (
    <AnimatePresence>
      {isRenameInProcess && (
        <motion.div {...DEFAULT_PRESENCE_ANIMATION} className={styles.spinner}>
          <Spinner label={t('Переименовываем клан')} />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const classNameBase = cx(styles.base, {
    [styles.isNavalBaseEnabled]: settings.supply.isEnabled,
  });

  return (
    <div className={classNameBase}>
      <div className={styles.clanName}>{displayElement}</div>
      {spinner}
    </div>
  );
};

export default memo(OwnClanTitle);
